import React, { useState } from "react";
import { Select, TextInput, Button } from "flowbite-react";
import { HiSearch, HiX } from "react-icons/hi";
import { DateRangePicker } from "./DateRangePicker";
import type { TransactionFilters as TFilters } from "..";
import { useFamily } from "../../../domain/services/hooks/useFamily";
import { useFamilyBankAccounts } from "../../../domain/services/hooks/useFamilyBankAccounts";

export const TransactionFilters: React.FC<{
  filters: TFilters;
  onFilterChange: (filters: TFilters) => void;
}> = ({ filters, onFilterChange }) => {
  const { family } = useFamily();
  const { familyBankAccounts } = useFamilyBankAccounts();
  const [searchInput, setSearchInput] = useState(filters.searchTerm || "");

  const handleChange = (key: keyof TFilters, value: any) => {
    onFilterChange({ ...filters, [key]: value });
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    handleChange("searchTerm", searchInput);
  };

  const clearFilters = () => {
    setSearchInput("");
    onFilterChange({
      type: "all",
      member: "all",
      account: "all",
      groupBy: "none",
      searchTerm: "",
      dateRange: { start: null, end: null },
    });
  };

  const hasActiveFilters =
    filters.type !== "all" ||
    filters.member !== "all" ||
    filters.account !== "all" ||
    filters.groupBy !== "none" ||
    filters.searchTerm ||
    filters.dateRange?.start ||
    filters.dateRange?.end;

  return (
    <div className="p-4 border-b border-gray-200 dark:border-gray-700">
      <div className="flex flex-wrap gap-4 mb-4">
        <Select
          value={filters.type}
          onChange={(e) => handleChange("type", e.target.value)}
          className="w-48"
        >
          <option value="all">All Types</option>
          <option value="Income">Income</option>
          <option value="Expense">Expenses</option>
          <option value="Transfer">Transfers</option>
          <option value="Payment">Payments</option>
          <option value="Unknown">Unknown</option>
        </Select>

        <Select
          value={filters.member}
          onChange={(e) => handleChange("member", e.target.value)}
          className="w-48"
        >
          <option value="all">All Members</option>
          {family?.members?.map((member) => (
            <option key={member} value={member}>
              {member}
            </option>
          ))}
        </Select>

        <Select
          value={filters.account}
          onChange={(e) => handleChange("account", e.target.value)}
          className="w-48"
        >
          <option value="all">All Accounts</option>
          {Object.entries(familyBankAccounts || {}).map(([_userId, accounts]) =>
            accounts.map((account) => (
              <option key={account.accountId} value={account.accountId}>
                {account.name} - {account.mask}
              </option>
            )),
          )}
        </Select>

        <Select
          value={filters.groupBy}
          onChange={(e) =>
            handleChange("groupBy", e.target.value as TFilters["groupBy"])
          }
          className="w-48"
        >
          <option value="none">No Grouping</option>
          <option value="date">Group by Date</option>
          <option value="merchant">Group by Merchant</option>
          <option value="category">Group by Category</option>
        </Select>

        <DateRangePicker
          startDate={filters.dateRange?.start || null}
          endDate={filters.dateRange?.end || null}
          onChange={(range) => handleChange("dateRange", range)}
        />
      </div>

      <div className="flex items-center gap-4">
        <form onSubmit={handleSearch} className="flex-1">
          <TextInput
            type="search"
            icon={HiSearch}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search transactions..."
            className="w-full"
          />
        </form>

        {hasActiveFilters && (
          <Button
            color="light"
            size="sm"
            onClick={clearFilters}
            className="flex items-center gap-2"
          >
            <HiX className="w-4 h-4" />
            Clear Filters
          </Button>
        )}
      </div>
    </div>
  );
};
